/**
 * The log kept in THIS browser (IndexedDB) — the log-report kit's storage (website roadmap S09; later log sections reuse
 * it). From terms 19 it is a copy of what the Omni-Torn server keeps for you (own-history.ts: the reader catches it up
 * and sends each Torn read to the server), kept here for speed; below 19 only locally — it lets a report reach back past
 * the server's 90-day copy. One database for the site, rows per player:
 *  - `entries` (key [player, id]; index [player, at]) — log lines as app/read.ts reads them;
 *  - `spans` (key [player, stream]) — which stretches of a stream (the whole log, or one category) were read in full.
 * Kept until the player deletes it ("Forget" on a report page) — signing out deletes nothing (owner, 2026-10-30).
 * Without IndexedDB (a private window, blocked storage) it works in memory for the visit.
 */
import { mergeSpans, type DateRange, type Span } from '@omni-torn/shared';
import type { LogEntry } from '../read';

const DB_NAME = 'omni-torn-logs';
const DB_VERSION = 1;

interface Row extends LogEntry {
  p: number;
}

export interface LogStore {
  /** False when kept only in memory for this visit. */
  readonly lasting: boolean;
  /** Adds what isn't kept yet; answers how many were new. */
  put(entries: readonly LogEntry[]): Promise<number>;
  /** Entries in a range (newest first), optionally only those a test keeps. */
  range(r: DateRange, keep?: (e: LogEntry) => boolean): Promise<LogEntry[]>;
  count(): Promise<number>;
  spans(stream: string): Promise<Span[]>;
  addSpan(stream: string, span: Span): Promise<Span[]>;
  /** How far this copy has caught up with the server's (its order of keeping), per server stream. */
  seq(name: string): Promise<number>;
  setSeq(name: string, seq: number): Promise<void>;
}

// ---------------------------------------------------------------- memory (fallback)

const memory = new Map<number, { entries: Map<string, LogEntry>; spans: Map<string, Span[]>; seq?: Map<string, number> }>();

function memoryStore(p: number): LogStore {
  const mine = () => {
    let m = memory.get(p);
    if (!m) memory.set(p, (m = { entries: new Map(), spans: new Map() }));
    return m;
  };
  return {
    lasting: false,
    async put(entries) {
      const m = mine().entries;
      let added = 0;
      for (const e of entries) if (!m.has(e.id)) (m.set(e.id, e), added++);
      return added;
    },
    async range(r, keep) {
      return [...mine().entries.values()].filter((e) => (r.from === null || e.at >= r.from) && (r.to === null || e.at <= r.to) && (!keep || keep(e))).sort((a, b) => b.at - a.at);
    },
    async count() {
      return mine().entries.size;
    },
    async spans(stream) {
      return mine().spans.get(stream) ?? [];
    },
    async addSpan(stream, span) {
      const s = mergeSpans([...(mine().spans.get(stream) ?? []), span]);
      mine().spans.set(stream, s);
      return s;
    },
    async seq(name) {
      return mine().seq?.get(name) ?? 0;
    },
    async setSeq(name, seq) {
      const m = mine();
      (m.seq ??= new Map()).set(name, seq);
    },
  };
}

// ---------------------------------------------------------------- IndexedDB

let opening: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (opening) return opening;
  opening = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('entries')) db.createObjectStore('entries', { keyPath: ['p', 'id'] }).createIndex('at', ['p', 'at']);
        if (!db.objectStoreNames.contains('spans')) db.createObjectStore('spans', { keyPath: ['p', 'stream'] });
      };
      req.onsuccess = () => {
        const db = req.result;
        // Another tab deleting it (sign-out there): let go so the delete can finish.
        db.onversionchange = () => {
          db.close();
          opening = null;
        };
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

const done = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('The browser couldn’t keep the log.'));
    tx.onabort = () => reject(tx.error ?? new Error('The browser couldn’t keep the log.'));
  });

const ask = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('The browser couldn’t read the log.'));
  });

function dbStore(db: IDBDatabase, p: number): LogStore {
  return {
    lasting: true,
    async put(entries) {
      if (!entries.length) return 0;
      const tx = db.transaction('entries', 'readwrite');
      const os = tx.objectStore('entries');
      let added = 0;
      for (const e of entries) {
        const row: Row = { p, id: e.id, type: e.type, at: e.at, title: e.title, category: e.category, data: e.data };
        const req = os.add(row);
        req.onsuccess = () => void added++;
        // Already kept: not an error worth stopping for.
        req.onerror = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        };
      }
      await done(tx);
      return added;
    },
    async range(r, keep) {
      const tx = db.transaction('entries', 'readonly');
      const idx = tx.objectStore('entries').index('at');
      const rows = await ask(idx.getAll(IDBKeyRange.bound([p, r.from ?? 0], [p, r.to ?? Number.MAX_SAFE_INTEGER])));
      const out: LogEntry[] = [];
      for (let i = rows.length - 1; i >= 0; i--) {
        const { p: _p, ...e } = rows[i] as Row;
        if (!keep || keep(e)) out.push(e);
      }
      return out;
    },
    async count() {
      const tx = db.transaction('entries', 'readonly');
      return ask(tx.objectStore('entries').index('at').count(IDBKeyRange.bound([p, 0], [p, Number.MAX_SAFE_INTEGER])));
    },
    async spans(stream) {
      const tx = db.transaction('spans', 'readonly');
      const row = (await ask(tx.objectStore('spans').get([p, stream]))) as { spans?: Span[] } | undefined;
      return row?.spans ?? [];
    },
    async addSpan(stream, span) {
      const tx = db.transaction('spans', 'readwrite');
      const os = tx.objectStore('spans');
      const row = (await ask(os.get([p, stream]))) as { spans?: Span[] } | undefined;
      const spans = mergeSpans([...(row?.spans ?? []), span]);
      os.put({ p, stream, spans });
      await done(tx);
      return spans;
    },
    // Kept beside the spans, under a name no stream has ("#seq:log").
    async seq(name) {
      const tx = db.transaction('spans', 'readonly');
      const row = (await ask(tx.objectStore('spans').get([p, `#seq:${name}`]))) as { seq?: number } | undefined;
      return Number(row?.seq ?? 0) || 0;
    },
    async setSeq(name, seq) {
      const tx = db.transaction('spans', 'readwrite');
      tx.objectStore('spans').put({ p, stream: `#seq:${name}`, seq });
      await done(tx);
    },
  };
}

/** The signed-in player's kept log (IndexedDB, else memory for this visit). */
export async function logStore(playerId: number): Promise<LogStore> {
  const db = await openDb();
  return db ? dbStore(db, playerId) : memoryStore(playerId);
}

/** Delete everything kept in this browser (every player): "Forget" on a report page. */
export async function forgetLogs(): Promise<void> {
  memory.clear();
  const db = await opening?.catch(() => null);
  db?.close();
  opening = null;
  if (typeof indexedDB === 'undefined') return;
  await new Promise<void>((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

